/**
 * Deterministic typography clean-up for protocol section HTML — the style-guide
 * rules a language model can't reliably follow character by character, applied
 * after generation instead of asked for in the prompt (Dizzaroo style checks
 * #7, #10, #24, #32):
 *
 *   - straight quotes/apostrophes -> curly (“ ” ‘ ’)
 *   - "e.g.," / "i.e.," -> "eg," / "ie," — the style pack's default (AMA) form, so
 *     model-written and template text end up in one consistent style
 *   - exactly one space after . ! ? :
 *   - ISMP trailing zeros in doses: "8.0 mg/kg" -> "8 mg/kg" (whole lists like
 *     "3.0, 6.0, 8.0 mg/kg" together), but never concentrations/lab values
 *     ("Hemoglobin ≥ 9.0 g/dL" is unchanged)
 *   - one space after a comparison operator in running text ("≥18" -> "≥ 18",
 *     "<=100" -> "≤ 100"); tables keep their compact notation
 *   - URLs and unit symbols mis-capitalized by older sentence-capitalization runs
 *     ("Https://", "Mg/dL"); "doi:10.x/…" references rewritten as https://doi.org/10.x/…
 *
 * Only text between tags is touched — tags, attributes and comments pass through
 * unchanged, as does anything inside <code>/<pre>. Idempotent: it runs on every
 * display/export via finalizeGeneratedProtocolOutput, so a second pass must
 * change nothing.
 */

// A real tag starts with a letter (or "/" + letter) — so a bare "< 20 IU/mL" in
// text is never mistaken for one.
const TOKEN_RE = /(<!--[\s\S]*?-->|<\/?[A-Za-z][^<>]*>)/;
const BLOCK_TAG_RE = /^<\/?(p|li|td|th|tr|h[1-6]|div|br|ul|ol|table|caption|blockquote|dd|dt)\b/i;
const VERBATIM_OPEN_RE = /^<(code|pre)\b/i;
const VERBATIM_CLOSE_RE = /^<\/(code|pre)\b/i;

const QUOTE_ENTITIES = [
  [/&quot;|&#34;|&#x22;/gi, '"'],
  [/&#39;|&apos;|&#x27;/gi, "'"],
];

// A quote counts as opening when it follows nothing, whitespace, an opening
// bracket/dash/slash, or another opening quote.
const OPENING_CONTEXT_RE = /[\s([{—–/\-“‘]/;

function curlyQuotes(text, prevChar) {
  let out = '';
  let prev = prevChar;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      out += !prev || OPENING_CONTEXT_RE.test(prev) ? '“' : '”';
    } else if (ch === "'") {
      const next = text[i + 1] || '';
      const opens = (!prev || OPENING_CONTEXT_RE.test(prev)) && /[A-Za-z]/.test(next);
      out += opens ? '‘' : '’';
    } else {
      out += ch;
    }
    prev = out[out.length - 1];
  }
  return out;
}

const DOSE_UNIT = '(?:mg|mcg|µg|g|mL|L)';
// A run of numbers sharing one dose unit: "8.0 mg", "0.5 to 8.0 mg/kg",
// "1.6, 3.0, 4.5, 6.0, and 8.0 mg/kg".
const DOSE_RUN_RE = new RegExp(
  `(?<![\\d.])(\\d+(?:\\.\\d+)?(?:(?:\\s*,\\s*(?:and\\s+|or\\s+)?|\\s+(?:and|or|to)\\s+|\\s*[-–]\\s*)\\d+(?:\\.\\d+)?)*)(\\s*${DOSE_UNIT})(?![A-Za-z])(\\/[A-Za-z0-9²]+)?`,
  'g',
);
// Per-volume denominators mark a concentration or lab value, not a dose.
const CONCENTRATION_DENOMINATOR_RE = /^\/(?:dL|L|mL|µL|uL|l|ml|dl)$/;

function stripDoseTrailingZeros(text) {
  return text.replace(DOSE_RUN_RE, (match, numbers, unit, denominator) => {
    if (denominator && CONCENTRATION_DENOMINATOR_RE.test(denominator)) return match;
    const fixed = numbers.replace(/(\d+)\.0+(?!\d)/g, '$1');
    return `${fixed}${unit}${denominator || ''}`;
  });
}

// Comparison operators directly against a number in running text: "≥18 years" ->
// "≥ 18 years", "<=100 mg" -> "≤ 100 mg". Matches the escaped (&lt;/&gt;) and raw
// forms. Never applied inside tables, where compact "±1 day" / "≥3" is standard,
// and URLs are left alone (no "=" handling at all).
function spaceComparisonOperators(text) {
  return text
    .replace(/(?:&lt;|<)=\s*(?=\d)/g, '≤ ')
    .replace(/(?:&gt;|>)=\s*(?=\d)/g, '≥ ')
    .replace(/(≤|≥|±|&lt;|&gt;|<|>)(?=\d)/g, '$1 ');
}

function cleanTextSegment(text, prevChar, inTable) {
  let out = text;
  if (!inTable) out = spaceComparisonOperators(out);
  QUOTE_ENTITIES.forEach(([re, ch]) => {
    out = out.replace(re, ch);
  });
  out = out.replace(/\be\.g\.,?(?=\s)/g, 'eg,').replace(/\bi\.e\.,?(?=\s)/g, 'ie,');
  out = out.replace(/([.!?:]) {2,}(?=\S)/g, '$1 ');
  out = out.replace(/\bHttps?:\/\//g, (m) => m.toLowerCase()).replace(/\bWww\./g, 'www.');
  // DOI in AMA URL form with no trailing period: "Doi:10.1056/NEJMoa1607303." ->
  // "https://doi.org/10.1056/NEJMoa1607303" (style check #47).
  out = out.replace(/\bdoi:\s*(10\.\d{4,9}\/[^\s<]+?)\.?(?=\s|$)/gi, 'https://doi.org/$1');
  // Unit symbols capitalized by older sentence-capitalization runs at the start of
  // a table cell ("Mg/dL", "G/dL", "Mmol/L") — unit symbols are never capitalized.
  out = out.replace(
    /\b(Mg|Mcg|Kg|G|Ng|Pg|Mmol|Nmol|Pmol|Ml|Dl)(?=\/(?:dL|L|kg|mL|m2|m²|day|d|h)\b)/g,
    (m) => m.charAt(0).toLowerCase() + m.slice(1),
  );
  out = stripDoseTrailingZeros(out);
  return curlyQuotes(out, prevChar);
}

export function applyProtocolTypography(html) {
  if (!html) return html;
  const parts = String(html).split(TOKEN_RE);
  let prevChar = '';
  let verbatimDepth = 0;
  let tableDepth = 0;
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (!part) continue;
    if (i % 2 === 1) {
      // Tag or comment.
      if (VERBATIM_OPEN_RE.test(part)) verbatimDepth += 1;
      else if (VERBATIM_CLOSE_RE.test(part)) verbatimDepth = Math.max(0, verbatimDepth - 1);
      if (/^<table\b/i.test(part)) tableDepth += 1;
      else if (/^<\/table\b/i.test(part)) tableDepth = Math.max(0, tableDepth - 1);
      if (BLOCK_TAG_RE.test(part)) prevChar = '';
      continue;
    }
    if (verbatimDepth === 0) parts[i] = cleanTextSegment(part, prevChar, tableDepth > 0);
    prevChar = parts[i][parts[i].length - 1];
  }
  return parts.join('');
}
